import { useState, useRef, useEffect } from "react";
import { User, Mail, Phone, Camera, Save, Loader2, CreditCard, CheckCircle2, Building2, Badge } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getPlanPrice } from "@/lib/planLimits";

const ProfileSection = () => {
  const { user, profile, role } = useAuth();
  const [subscription, setSubscription] = useState<{ plan: string; status: string; trial_end: string; activated_at: string | null } | null>(null);

  useEffect(() => {
    if (!user?.id || role !== "business_owner") return;
    supabase.from("subscriptions").select("*").eq("user_id", user.id).maybeSingle().then(({ data }) => {
      if (data) setSubscription(data as any);
    });
  }, [user?.id, role]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: profile?.full_name || "",
    phone: profile?.phone || "",
    email: profile?.email || user?.email || "",
    company_name: profile?.company_name || "",
    operator_license: profile?.operator_license || "",
  });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleStartEdit = () => {
    setForm({
      full_name: profile?.full_name || "",
      phone: profile?.phone || "",
      email: profile?.email || user?.email || "",
      company_name: profile?.company_name || "",
      operator_license: profile?.operator_license || "",
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!user?.id) return;
    if (!form.full_name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: form.full_name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        company_name: form.company_name.trim() || null,
        operator_license: form.operator_license.trim() || null,
      })
      .eq("id", user.id);
    setSaving(false);
    if (error) {
      toast.error("Failed to update profile");
      return;
    }
    toast.success("Profile updated successfully");
    setEditing(false);
    // Refresh the page to pick up new profile data
    window.location.reload();
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2MB");
      return;
    }
    setAvatarUploading(true);
    const ext = file.name.split(".").pop();
    const path = `avatars/${user.id}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from("avatars")
      .upload(path, file, { upsert: true });
    if (uploadError) {
      toast.error("Failed to upload avatar");
      setAvatarUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
    await supabase.from("profiles").update({ avatar_url: urlData.publicUrl }).eq("id", user.id);
    setAvatarUploading(false);
    toast.success("Avatar updated");
    window.location.reload();
  };

  const initials = (profile?.full_name || user?.email || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="space-y-6">
      <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">MY PROFILE</h2>

      <div className="app-card rounded-2xl p-6 md:p-8 max-w-2xl">
        <div className="h-px -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-gold)), transparent)" }} />

        {/* Avatar */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative group">
            {profile?.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-2 border-[hsl(var(--app-gold)/0.4)]"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[hsl(var(--app-gold)/0.15)] border-2 border-[hsl(var(--app-gold)/0.4)] flex items-center justify-center">
                <span className="font-bebas text-2xl text-[hsl(var(--app-gold))]">{initials}</span>
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={avatarUploading}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] flex items-center justify-center hover:brightness-110 transition-all shadow-lg"
            >
              {avatarUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
          </div>
          <p className="mt-3 font-bebas text-lg text-[hsl(var(--app-text-primary))]">
            {profile?.full_name || "No Name Set"}
          </p>
          <p className="text-xs text-[hsl(var(--app-text-muted))]">{role?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase()) || "User"}</p>
        </div>

        {/* Fields */}
        <div className="space-y-4">
          <ProfileField
            icon={<User className="w-4 h-4" />}
            label="Full Name"
            value={editing ? form.full_name : profile?.full_name || "—"}
            editing={editing}
            onChange={(v) => setForm((f) => ({ ...f, full_name: v }))}
          />
          <ProfileField
            icon={<Mail className="w-4 h-4" />}
            label="Email"
            value={editing ? form.email : profile?.email || user?.email || "—"}
            editing={editing}
            onChange={(v) => setForm((f) => ({ ...f, email: v }))}
            type="email"
          />
          <ProfileField
            icon={<Phone className="w-4 h-4" />}
            label="Phone"
            value={editing ? form.phone : profile?.phone || "—"}
            editing={editing}
            onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
            type="tel"
          />
          <ProfileField
            icon={<Building2 className="w-4 h-4" />}
            label="Business / Company Name"
            value={editing ? form.company_name : profile?.company_name || "—"}
            editing={editing}
            onChange={(v) => setForm((f) => ({ ...f, company_name: v }))}
          />
          <ProfileField
            icon={<Badge className="w-4 h-4" />}
            label="Operator / Registration Details"
            value={editing ? form.operator_license : profile?.operator_license || "—"}
            editing={editing}
            onChange={(v) => setForm((f) => ({ ...f, operator_license: v }))}
          />
        </div>

        {/* Actions */}
        <div className="mt-8 flex gap-3">
          {editing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-semibold text-sm hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-6 py-3 rounded-xl border border-[hsl(var(--app-border-active))] text-[hsl(var(--app-text-secondary))] text-sm hover:text-[hsl(var(--app-text-primary))] transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={handleStartEdit}
              className="px-6 py-3 rounded-xl border border-[hsl(var(--app-gold)/0.4)] text-[hsl(var(--app-gold))] text-sm font-semibold hover:bg-[hsl(var(--app-gold)/0.08)] transition-all"
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Subscription / Plan - only for security operators */}
      {role === "business_owner" && <div className="app-card rounded-2xl p-6 md:p-8 max-w-2xl">
        <div className="h-px -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-gold)), transparent)" }} />
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--app-gold)/0.1)] border border-[hsl(var(--app-gold)/0.2)] flex items-center justify-center text-[hsl(var(--app-gold))]">
            <CreditCard className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bebas text-lg text-[hsl(var(--app-text-primary))]">CURRENT PLAN</h3>
            <p className="text-xs text-[hsl(var(--app-text-muted))]">Your active subscription details</p>
          </div>
        </div>

        {subscription ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))]">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-[hsl(var(--app-text-muted))] mb-1">Plan</p>
                <p className="font-bebas text-xl text-[hsl(var(--app-gold))]">
                  {subscription.status === 'trial' || subscription.status === 'trialing' ? 'ALL FEATURES (TRIAL)' : subscription.plan.toUpperCase().replace('_', ' ')}
                </p>
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-[hsl(var(--app-text-muted))] mb-1">Status</p>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase ${
                  subscription.status === "active" || subscription.status === "trialing" || subscription.status === "trial"
                    ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]"
                    : "bg-red-500/15 text-red-400"
                }`}>
                  <CheckCircle2 className="w-3 h-3" />
                  {subscription.status === "trialing" || subscription.status === "trial" ? "Free Trial" : subscription.status}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))]">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-[hsl(var(--app-text-muted))] mb-1">Price</p>
                <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">
                  {subscription.status === 'trial' || subscription.status === 'trialing' ? 'Free' : getPlanPrice(subscription.plan)}
                  {subscription.status !== 'trial' && subscription.status !== 'trialing' && <span className="text-[hsl(var(--app-text-muted))] font-normal">/month</span>}
                </p>
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-[hsl(var(--app-text-muted))] mb-1">
                  {subscription.status === "trialing" || subscription.status === "trial" ? "Trial Ends" : "Since"}
                </p>
                <p className="text-sm text-[hsl(var(--app-text-primary))]">
                  {new Date((subscription.status === "trialing" || subscription.status === "trial") ? subscription.trial_end : (subscription.activated_at || subscription.trial_end)).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-center">
            <p className="text-sm text-[hsl(var(--app-text-muted))]">No active subscription</p>
            <p className="text-xs text-[hsl(var(--app-text-muted))] mt-1">Visit the Billing tab to choose a plan</p>
          </div>
        )}
      </div>}
    </div>
  );
};

const ProfileField = ({
  icon,
  label,
  value,
  editing,
  onChange,
  type = "text",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  editing: boolean;
  onChange: (v: string) => void;
  type?: string;
}) => (
  <div className="flex items-center gap-4 p-4 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))]">
    <div className="w-9 h-9 rounded-lg bg-[hsl(var(--app-gold)/0.1)] border border-[hsl(var(--app-gold)/0.2)] flex items-center justify-center flex-shrink-0 text-[hsl(var(--app-gold))]">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--app-text-muted))] mb-0.5">{label}</p>
      {editing ? (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent text-sm text-[hsl(var(--app-text-primary))] outline-none border-b border-[hsl(var(--app-gold)/0.3)] focus:border-[hsl(var(--app-gold))] pb-0.5 font-outfit transition-colors"
        />
      ) : (
        <p className="text-sm text-[hsl(var(--app-text-primary))] truncate">{value}</p>
      )}
    </div>
  </div>
);

export default ProfileSection;
